import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import ThemeToggle from "@/components/ThemeToggle";
import SettingsDialog from "@/components/SettingsDialog";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import FeedbackDialog from "@/components/FeedbackDialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Carrot, LogOut, Sparkles, MessageSquarePlus } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2" data-testid="app-logo">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-sm">
            <Carrot className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-extrabold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Carrot
          </span>
          <span
            data-testid="beta-badge"
            title="Beta — all Pro & Team features unlocked, free"
            className="hidden sm:inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary"
          >
            <Sparkles className="w-3 h-3" />
            Beta · Pro unlocked
          </span>
        </div>

        <div className="flex items-center gap-2">
          <WorkspaceSwitcher />
          <Button
            variant="ghost"
            size="sm"
            data-testid="feedback-header-btn"
            onClick={() => setFeedbackOpen(true)}
            className="hidden sm:inline-flex rounded-full gap-1.5 text-muted-foreground hover:text-foreground"
          >
            <MessageSquarePlus className="w-4 h-4" /> Feedback
          </Button>
          <SettingsDialog />
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="rounded-full outline-none ring-offset-2 focus-visible:ring-2 ring-primary">
                <Avatar className="w-9 h-9 border-2 border-primary/30">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-primary/10 text-primary font-bold">
                    {user?.name?.[0]?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="font-semibold">{user?.name}</span>
                  <span className="text-xs text-muted-foreground font-normal truncate">{user?.email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="feedback-menu-item" onClick={() => setFeedbackOpen(true)} className="sm:hidden cursor-pointer">
                <MessageSquarePlus className="w-4 h-4 mr-2" />
                Send feedback
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-destructive focus:text-destructive cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </header>
  );
}
